// src/theme/presets.ts

import type { TerminalTheme } from '../types';

export const THEMES: Record<string, TerminalTheme> = {
  'whss-dark': {
    id: 'whss-dark',
    name: 'WHSS Dark',
    type: 'dark',
    colors: {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#d4d4d4',
      cursorAccent: '#1e1e1e',
      selectionBackground: '#264f78',
      selectionForeground: '#ffffff',
      black: '#000000',
      red: '#cd3131',
      green: '#0dbc79',
      yellow: '#e5e510',
      blue: '#2472c8',
      magenta: '#bc3fbc',
      cyan: '#11a8cd',
      white: '#e5e5e5',
      brightBlack: '#666666',
      brightRed: '#f14c4c',
      brightGreen: '#23d18b',
      brightYellow: '#f5f543',
      brightBlue: '#3b8eea',
      brightMagenta: '#d670d6',
      brightCyan: '#29b8db',
      brightWhite: '#ffffff',
    },
  },
  'whss-light': {
    id: 'whss-light',
    name: 'WHSS Light',
    type: 'light',
    colors: {
      background: '#ffffff',
      foreground: '#383a42',
      cursor: '#383a42',
      cursorAccent: '#ffffff',
      selectionBackground: '#add6ff',
      selectionForeground: '#000000',
      black: '#383a42',
      red: '#e45649',
      green: '#50a14f',
      yellow: '#c18401',
      blue: '#4078f2',
      magenta: '#a626a4',
      cyan: '#0184bc',
      white: '#fafafa',
      brightBlack: '#4f525e',
      brightRed: '#e06c75',
      brightGreen: '#98c379',
      brightYellow: '#e5c07b',
      brightBlue: '#61afef',
      brightMagenta: '#c678dd',
      brightCyan: '#56b6c2',
      brightWhite: '#ffffff',
    },
  },
  'dracula': {
    id: 'dracula',
    name: 'Dracula',
    type: 'dark',
    colors: {
      background: '#282a36',
      foreground: '#f8f8f2',
      cursor: '#f8f8f2',
      cursorAccent: '#282a36',
      selectionBackground: '#44475a',
      black: '#21222c',
      red: '#ff5555',
      green: '#50fa7b',
      yellow: '#f1fa8c',
      blue: '#bd93f9',
      magenta: '#ff79c6',
      cyan: '#8be9fd',
      white: '#f8f8f2',
      brightBlack: '#6272a4',
      brightRed: '#ff6e6e',
      brightGreen: '#69ff94',
      brightYellow: '#ffffa5',
      brightBlue: '#d6acff',
      brightMagenta: '#ff92df',
      brightCyan: '#a4ffff',
      brightWhite: '#ffffff',
    },
  },
  'solarized-dark': {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    type: 'dark',
    colors: {
      background: '#002b36',
      foreground: '#839496',
      cursor: '#839496',
      cursorAccent: '#002b36',
      selectionBackground: '#073642',
      black: '#073642',
      red: '#dc322f',
      green: '#859900',
      yellow: '#b58900',
      blue: '#268bd2',
      magenta: '#d33682',
      cyan: '#2aa198',
      white: '#eee8d5',
      brightBlack: '#002b36',
      brightRed: '#cb4b16',
      brightGreen: '#586e75',
      brightYellow: '#657b83',
      brightBlue: '#839496',
      brightMagenta: '#6c71c4',
      brightCyan: '#93a1a1',
      brightWhite: '#fdf6e3',
    },
  },
  'monokai': {
    id: 'monokai',
    name: 'Monokai',
    type: 'dark',
    colors: {
      background: '#272822',
      foreground: '#f8f8f2',
      cursor: '#f8f8f2',
      cursorAccent: '#272822',
      selectionBackground: '#49483e',
      black: '#272822',
      red: '#f92672',
      green: '#a6e22e',
      yellow: '#f4bf75',
      blue: '#66d9ef',
      magenta: '#ae81ff',
      cyan: '#a1efe4',
      white: '#f8f8f2',
      brightBlack: '#75715e',
      brightRed: '#f92672',
      brightGreen: '#a6e22e',
      brightYellow: '#f4bf75',
      brightBlue: '#66d9ef',
      brightMagenta: '#ae81ff',
      brightCyan: '#a1efe4',
      brightWhite: '#f9f8f5',
    },
  },
  'high-contrast': {
    id: 'high-contrast',
    name: 'High Contrast',
    type: 'dark',
    colors: {
      // WCAG AAA compliant - 21:1 contrast ratio
      background: '#000000',
      foreground: '#ffffff',
      cursor: '#ffffff',
      cursorAccent: '#000000',
      selectionBackground: '#ffffff',
      selectionForeground: '#000000',
      // High contrast ANSI colors - all meet WCAG AA (4.5:1 minimum)
      black: '#000000',
      red: '#ff5555',      // Bright red for visibility
      green: '#55ff55',    // Bright green for visibility
      yellow: '#ffff55',   // Bright yellow for visibility
      blue: '#5555ff',     // Bright blue for visibility
      magenta: '#ff55ff',  // Bright magenta for visibility
      cyan: '#55ffff',     // Bright cyan for visibility
      white: '#ffffff',
      brightBlack: '#808080',  // Gray for contrast
      brightRed: '#ff5555',
      brightGreen: '#55ff55',
      brightYellow: '#ffff55',
      brightBlue: '#5555ff',
      brightMagenta: '#ff55ff',
      brightCyan: '#55ffff',
      brightWhite: '#ffffff',
    },
  },
  'high-contrast-light': {
    id: 'high-contrast-light',
    name: 'High Contrast Light',
    type: 'light',
    colors: {
      // WCAG AAA compliant - 21:1 contrast ratio
      background: '#ffffff',
      foreground: '#000000',
      cursor: '#000000',
      cursorAccent: '#ffffff',
      selectionBackground: '#000000',
      selectionForeground: '#ffffff',
      // High contrast ANSI colors for light background - all meet WCAG AA
      black: '#000000',
      red: '#990000',      // Dark red for contrast on white
      green: '#006600',    // Dark green for contrast on white
      yellow: '#666600',   // Dark yellow/olive for contrast on white
      blue: '#000099',     // Dark blue for contrast on white
      magenta: '#990099',  // Dark magenta for contrast on white
      cyan: '#006666',     // Dark cyan for contrast on white
      white: '#ffffff',
      brightBlack: '#666666',  // Dark gray for contrast
      brightRed: '#cc0000',
      brightGreen: '#008800',
      brightYellow: '#888800',
      brightBlue: '#0000cc',
      brightMagenta: '#cc00cc',
      brightCyan: '#008888',
      brightWhite: '#ffffff',
    },
  },
};
